<script lang="ts">
  import type { MarketOrdebooks, SailfishMessage } from "sailfish-sdk";
  import { onDestroy, onMount } from "svelte";
  import { SvelteMap } from "svelte/reactivity";
  import { route } from "@mateothegreat/svelte5-router";
  import {
    ArrowLeft,
    PlayIcon,
    OctagonPauseIcon,
    ClockIcon,
  } from "lucide-svelte";

  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/products/sailfish/polymarket"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Sailfish", url: "https://0xfire.com/products/sailfish" },
      { name: "Polymarket", url: "https://0xfire.com/products/sailfish/polymarket" },
    ]),
  ));

  // --- Reactive State ---
  let markets = new SvelteMap<string, MarketOrdebooks>();
  let isRunning = $state(false);
  let now = $state(Date.now());
  let showReconfirm = $state(false);

  // --- Configuration ---
  const IDLE_LIMIT = 60000;
  let idleTimer: any;
  let timerInterval: any;

  onMount(() => {
    timerInterval = setInterval(() => {
      now = Date.now();
    }, 1000);
  });

  // --- Logic Functions ---
  const parseEpoch = (slug: string) => {
    const parts = slug.split("-");
    const epoch = parseInt(parts[parts.length - 1]);
    return isNaN(epoch) ? 0 : epoch;
  };

  let sortedMarkets = $derived(
    Array.from(markets.values()).sort((a, b) => {
      const epochA = parseEpoch(a.market_slug);
      const epochB = parseEpoch(b.market_slug);
      return epochA - epochB;
    }),
  );

  const fmt = (val: string | number) => {
    const n = parseFloat(val.toString());
    return isNaN(n) ? "0.00" : n.toFixed(3);
  };

  const getVol = (levels: Record<string, string>) =>
    Object.values(levels).reduce((acc, v) => acc + parseFloat(v), 0);

  let totalVolume = $derived.by(() => {
    let vol = 0;
    for (const m of markets.values()) {
      vol += getVol(m.orderbook_0.bids.levels) + getVol(m.orderbook_0.asks.levels);
      vol += getVol(m.orderbook_1.bids.levels) + getVol(m.orderbook_1.asks.levels);
    }
    return vol;
  });

  const formatVol = (v: number) => {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return v.toFixed(2);
  };

  const getRemainingTime = (epochSeconds: number) => {
    const startTime = epochSeconds * 1000;
    const endTime = startTime + 15 * 60 * 1000;
    const diff = endTime - now;

    if (diff <= 0)
      return { text: "SETTLED", color: "text-red-500", percent: 100 };

    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);

    let color = "text-green-500";
    if (mins < 5) color = "text-yellow-500";
    if (mins < 2) color = "text-red-500";

    return {
      text: `${mins}:${secs.toString().padStart(2, "0")}`,
      color,
      percent: Math.max(0, 100 - (diff / (15 * 60 * 1000)) * 100),
    };
  };

  const sortBids = (levels: Record<string, string>) =>
    Object.entries(levels).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));

  const sortAsks = (levels: Record<string, string>) =>
    Object.entries(levels).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));

  // --- Idle & Stream Management ---
  function resetIdleTimer() {
    clearTimeout(idleTimer);
    if (isRunning && !showReconfirm) {
      idleTimer = setTimeout(() => {
        showReconfirm = true;
        isRunning = false;
        polymarket?.rest();
      }, IDLE_LIMIT);
    }
  }

  function stillHere() {
    showReconfirm = false;
    if (!polymarket) return;
    isRunning = true;
    polymarket.swim();
    resetIdleTimer();
  }

  $effect(() => {
    if (isRunning) {
      window.addEventListener("mousemove", resetIdleTimer);
      window.addEventListener("keydown", resetIdleTimer);
      resetIdleTimer();
    }
    return () => {
      window.removeEventListener("mousemove", resetIdleTimer);
      window.removeEventListener("keydown", resetIdleTimer);
      clearTimeout(idleTimer);
    };
  });

  const callbacks = {
    onMessage: (message: SailfishMessage) => {},
    onMarketOrdebooks: (orderbook: MarketOrdebooks) => {
      markets.set(orderbook.market_slug, orderbook);
    },
  };

  let polymarket: any = null;
  let ready = $state(false);

  onMount(async () => {
    const { PolymarketSailfish } = await import("sailfish-sdk");
    polymarket = new PolymarketSailfish({ filter: {}, callbacks });
    ready = true;
  });

  const startStream = () => {
    if (!polymarket) return;
    polymarket.swim();
    isRunning = true;
  };

  const pauseStream = () => {
    if (!polymarket) return;
    polymarket.rest();
    isRunning = false;
  };

  onDestroy(() => {
    polymarket?.rest();
    clearInterval(timerInterval);
  });
</script>

<!-- Idle Reconnect Modal -->
{#if showReconfirm}
  <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
    <div class="max-w-sm w-full bg-gray-950 border border-gray-800 rounded-sm p-8 text-center">
      <div class="flex justify-center mb-6">
        <ClockIcon class="size-10 text-sky-500" />
      </div>
      <h2 class="text-xl font-bold text-white mb-2">Still watching?</h2>
      <p class="text-gray-500 text-sm mb-8">
        Stream paused after 60s of inactivity to save bandwidth.
      </p>
      <button
        onclick={stillHere}
        class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold w-full transition-colors"
      >
        Resume Stream
      </button>
    </div>
  </div>
{/if}

<div class="min-h-screen bg-black text-gray-300 selection:bg-sky-900 selection:text-white">
  <div class="max-w-7xl mx-auto px-4 py-8">
    <!-- Back link -->
    <a use:route href="/products/sailfish" class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors mb-8">
      <ArrowLeft class="size-4" /> Back to Sailfish
    </a>

    <div class="text-center mb-8">
      <div class="badge badge-outline rounded-none uppercase tracking-widest font-mono text-xs text-sky-500 border-sky-500/50 p-3 mb-4">
        Polymarket &middot; Live Demo
      </div>
      <h1 class="text-3xl md:text-4xl font-bold text-white tracking-tight">
        Sailfish Polymarket
      </h1>
    </div>

    {#if isRunning || markets.size > 0}
      <!-- Play/Pause (visible once streaming has started) -->
      <div class="flex gap-4 p-4 justify-center">
        <button
          onclick={() => (isRunning ? pauseStream() : startStream())}
          class="btn {isRunning
            ? 'btn-error'
            : 'bg-sky-600 hover:bg-sky-500 text-white border-none'} rounded-sm uppercase tracking-widest font-bold transition-colors"
        >
          {#if isRunning}
            <OctagonPauseIcon class="size-4 me-2 inline-block" />
            Pause
          {:else}
            <PlayIcon class="size-4 me-2 inline-block" />
            Resume
          {/if}
        </button>
      </div>

      <!-- Stats -->
      <div class="flex flex-wrap gap-4 justify-center my-6">
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm px-5 py-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Markets</p>
          <p class="text-lg font-black text-white tabular-nums">{markets.size}</p>
        </div>
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm px-5 py-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Total Volume</p>
          <p class="text-lg font-black text-white tabular-nums">${formatVol(totalVolume)}</p>
        </div>
      </div>

      <!-- Market Cards Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {#each sortedMarkets as market (market.market_slug)}
          {@const epoch = parseEpoch(market.market_slug)}
          {@const timeLeft = getRemainingTime(epoch)}
          {@const upVol = getVol(market.orderbook_0.bids.levels) + getVol(market.orderbook_0.asks.levels)}
          {@const downVol = getVol(market.orderbook_1.bids.levels) + getVol(market.orderbook_1.asks.levels)}
          {@const totalVol = upVol + downVol || 1}

          <div class="bg-gray-950/40 border border-gray-800 rounded-sm overflow-hidden">
            <!-- Time progress bar -->
            <div class="h-0.5 bg-gray-800 w-full">
              <div
                class="h-full bg-sky-600 transition-all duration-1000"
                style="width: {timeLeft.percent}%"
              ></div>
            </div>

            <!-- Card Header -->
            <div class="px-4 pt-3 pb-2">
              <div class="flex justify-between items-center mb-1">
                <div class="flex items-center gap-2">
                  <span class="text-[10px] font-mono text-gray-600 tabular-nums">
                    {new Date(epoch * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span class="text-[10px] font-mono font-bold {timeLeft.color} tabular-nums">
                    {timeLeft.text}
                  </span>
                </div>
                <span class="text-[10px] font-mono text-gray-500 tabular-nums">${fmt(totalVol)}</span>
              </div>
              <p class="text-sm font-bold text-white truncate">
                {market.question.split(" - ")[0]}
              </p>
            </div>

            <!-- Orderbook -->
            <div class="grid grid-cols-2 border-y border-gray-800">
              <!-- UP SIDE -->
              <div class="border-r border-gray-800">
                <div class="px-3 py-1 border-b border-gray-800/60">
                  <span class="text-[10px] font-mono text-green-500 uppercase tracking-widest">Yes</span>
                </div>
                <div class="px-3 py-2 space-y-0.5 font-mono text-[10px]">
                  {#each sortAsks(market.orderbook_0.asks.levels).slice(0, 6).reverse() as [p, v]}
                    <div class="flex justify-between text-red-500/40">
                      <span>{fmt(p)}</span><span class="tabular-nums">{fmt(v)}</span>
                    </div>
                  {/each}
                  <div class="h-px bg-gray-800 my-1"></div>
                  {#each sortBids(market.orderbook_0.bids.levels).slice(0, 6) as [p, v]}
                    <div class="flex justify-between text-green-500">
                      <span>{fmt(p)}</span><span class="tabular-nums">{fmt(v)}</span>
                    </div>
                  {/each}
                </div>
              </div>

              <!-- DOWN SIDE -->
              <div>
                <div class="px-3 py-1 border-b border-gray-800/60 text-right">
                  <span class="text-[10px] font-mono text-red-500 uppercase tracking-widest">No</span>
                </div>
                <div class="px-3 py-2 space-y-0.5 font-mono text-[10px] text-right">
                  {#each sortAsks(market.orderbook_1.asks.levels).slice(0, 6).reverse() as [p, v]}
                    <div class="flex justify-between text-red-500/40">
                      <span>{fmt(v)}</span><span class="tabular-nums">{fmt(p)}</span>
                    </div>
                  {/each}
                  <div class="h-px bg-gray-800 my-1"></div>
                  {#each sortBids(market.orderbook_1.bids.levels).slice(0, 6) as [p, v]}
                    <div class="flex justify-between text-green-500">
                      <span>{fmt(v)}</span><span class="tabular-nums">{fmt(p)}</span>
                    </div>
                  {/each}
                </div>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="px-4 py-2">
              <span class="text-[10px] font-mono text-gray-700 tabular-nums">Epoch {epoch}</span>
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <div class="flex flex-col items-center justify-center py-24 gap-6">
        <p class="text-gray-500 text-sm">Live Polymarket orderbooks, powered by Sailfish</p>
        <button
          onclick={startStream}
          class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors px-8 py-3"
        >
          <PlayIcon class="size-4 me-2 inline-block" />
          Start Streaming
        </button>
        <p class="text-[10px] font-mono text-gray-700">Real-time orderbook updates for all active markets</p>
      </div>
    {/if}
  </div>
</div>
